/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from '@/components/button'
import { Checkmark, Close } from '@/components/icons'
import { Select } from '@/components/input'
import { MatchItemLoading } from '@/components/skeleton'
import { LIMIT, OFFSET } from '@/constants/Common'
import { useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { useGetBracketMatches } from '@/services'
import { BracketMatch } from '@/types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useMediaQuery } from 'usehooks-ts'
import { eventSelector } from '../selectors'
import { BracketMatchItem } from './BracketMatchItem'

type Tag = {
  label: string
  value: string
  round: number[]
  showIcon: boolean
}

const filters: Tag[] = [
  {
    label: 'East',
    value: 'EAST',
    round: [0, 2, 3, 4],
    showIcon: false,
  },
  { label: 'West', value: 'WEST', round: [0, 2, 3, 4], showIcon: false },
  { label: 'North', value: 'NORTH', round: [0, 3, 4], showIcon: false },
  { label: 'South', value: 'SOUTH', round: [0, 3, 4], showIcon: false },
  { label: 'North East', value: 'NORTH_EAST', round: [0, 4], showIcon: false },
  { label: 'South West', value: 'SOUTH_WEST', round: [0, 4], showIcon: false },
  { label: 'North West', value: 'NORTH_WEST', round: [0, 4], showIcon: false },
  { label: 'South East', value: 'SOUTH_EAST', round: [0, 4], showIcon: false },
]

export const BracketMatches = () => {
  const { t } = useTranslation()

  const params = useParams()
  const { token } = useAppSelector(authSelector)
  const { bracket } = useAppSelector(eventSelector)

  const isMobile = useMediaQuery('(max-width: 767px)')

  const { bracketId } = params

  const { mutate: getMatches, data: resMatches, isLoading } = useGetBracketMatches()

  const onMapRoundOptions = () => {
    const roundOptions: any = []
    if (bracket) {
      for (let i = 0; i < bracket.totalRounds + 1; i += 1) {
        if (i === 0) {
          roundOptions.push({ label: 'ViewAll', key: 0, value: 0 })
        } else {
          roundOptions.push({ label: `Round${i}`, key: i, value: i })
        }
      }
      return roundOptions
    }
    return roundOptions
  }

  const roundOptions = useMemo(() => onMapRoundOptions(), [bracket])

  const [offset, setOffset] = useState(OFFSET)
  const [matches, setMatches] = useState<BracketMatch[]>([])
  const [selected, setSelected] = useState<any>(roundOptions[0])
  const [filterState, setFilterState] = useState<Tag[]>([])
  const [lastElement, setLastElement] = useState<any>(null)

  const observer = useRef(
    new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first.isIntersecting) {
        setOffset((pre) => pre + LIMIT)
      }
    })
  )

  const onFilterByRound = (value: number) => {
    const findValue = roundOptions.find((item: any) => item?.value === value)
    if (findValue) {
      setMatches([])
      setSelected(findValue)
    }
  }

  const handleFilter = (index: number) => {
    setMatches([])
    const currentFilters = [...filterState]
    currentFilters[index].showIcon = !currentFilters[index].showIcon
    setFilterState(currentFilters)
  }

  const onClearFilter = () => {
    const currentFilters = [...filterState]
    const newFilters = currentFilters.map((item) => {
      return {
        ...item,
        showIcon: false,
      }
    })
    setFilterState(newFilters)
    setMatches([])
    setOffset(OFFSET)
  }

  const onGetMatches = () => {
    if (!isMobile && token) {
      const tags = filterState.filter((el) => el.showIcon).map((item) => item?.value)
      getMatches({
        round: selected.key as number,
        bracketId: Number(bracketId),
        limit: LIMIT,
        offset: offset,
        tags: tags,
      })
    }
  }

  useEffect(() => {
    onGetMatches()
  }, [offset, selected, filterState])

  useEffect(() => {
    if (resMatches) setMatches([...matches, ...resMatches.result.hits])
  }, [resMatches])

  useEffect(() => {
    if (bracket?.elimination === 'COMPASS') {
      const orgFilters = JSON.parse(JSON.stringify(filters))
      const filterData = orgFilters.filter((item: Tag) => item?.round.includes(selected?.key as number))
      setFilterState(filterData)
    }
  }, [bracket, selected])

  useEffect(() => {
    const currentElement = lastElement
    const currentObserver = observer.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [lastElement])

  return (
    <div className="flex flex-col gap-y-2">
      <div className="flex gap-2 flex-wrap mb-2">
        <Select
          placeholder={t('common.select')}
          options={roundOptions}
          value={selected}
          onChange={(value) => onFilterByRound(value)}
          containerClassName="!w-40"
          optionsClassName="!rounded"
          buttonClassName="!bg-primary-500 !border-none !py-1 !text-white !font-semibold"
          optionClassName="!text-navy-900 !px-4"
          activeOptionClassName="!bg-primary-100"
        />
        {filterState.map((item, index) => (
          <Button
            key={item?.value}
            className={`text-xs font-semibold text-white border border-primary-500 ${
              item?.showIcon && 'bg-opacity-50'
            } bg-primary-500 bg-opacity-5 px-2 sm:px-3 py-1 flex flex-row gap-x-1 items-center`}
            onClick={() => handleFilter(index)}
          >
            {item?.showIcon && <Checkmark className="w-4 h-4 text-white" />}
            {item?.label}
          </Button>
        ))}
        {filterState.length ? (
          <div className="flex items-center">
            <Button
              className="text-xs text-white hidden sm:flex font-semibold hover:text-opacity-70"
              onClick={onClearFilter}
            >
              {t('common.clearFilters')}
            </Button>
            <Button className="flex sm:hidden" onClick={onClearFilter}>
              <Close className="text-white hover:text-opacity-70" />
            </Button>
          </div>
        ) : null}
      </div>
      {matches?.map((item, index) => {
        return index === matches.length - 1 && resMatches?.result.hasMore ? (
          <div key={item?.matchId ? item?.matchId : index} ref={setLastElement}>
            <BracketMatchItem
              match={item}
              bracketId={bracketId}
              callbackFetch={() => {
                setMatches([])
                setOffset(OFFSET)
                onGetMatches()
              }}
            />
          </div>
        ) : (
          <BracketMatchItem
            key={item?.matchId ? item?.matchId : index}
            match={item}
            bracketId={bracketId}
            callbackFetch={() => {
              setMatches([])
              setOffset(OFFSET)
              onGetMatches()
            }}
          />
        )
      })}
      {isLoading && (
        <>
          <MatchItemLoading />
          <MatchItemLoading />
          <MatchItemLoading />
        </>
      )}
      {resMatches && !resMatches?.result.hasMore && (
        <div className="flex justify-center">
          <span className="text-white font-semibold text-opacity-60">{t('common.noMoreResults')}</span>
        </div>
      )}
    </div>
  )
}
